import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import isFunction from 'lodash/isFunction';

import {
  ActionList, Checkbox, Details, Popover, useDetails,
} from '@primer/react';

import { DROPDOWN_DIRECTIONS } from '../../../constants/primer.constants';

import Button from '../Button/Button';
import CaretDownIcon from '../icons/CaretDownIcon';
import CaretUpIcon from '../icons/CaretUpIcon';
import Icon from '../icons/Icon';
import Label from '../Labels/Label';
import LinkButton from '../Labels/LinkButton';
import SearchInput from '../TextInputs/SearchInput/SearchInput';

import { getThemeColor } from '../../../utils/theme-utils';

const SelectionItem = ({
  id, label, render, isSelected, onValueSelected
}) => {
  if (isFunction(render)) {
    return (
      <ActionList.Item
        className={isSelected ? 'bg-border-dark' : 'bg-white hover:bg-border-light'}
        value={id}
      >
        <div className="flex w-full">
          <Checkbox
            className="mr-4"
            id={`selection-checkbox-${id}`}
            checked={isSelected}
            onChange={() => onValueSelected(id)}
          />
          {
            render({
              id, label, isSelected, onValueSelected
            })
          }
        </div>
      </ActionList.Item>
    )
  }

  return (
    <ActionList.Item
      className={isSelected ? 'bg-border-dark' : 'bg-white hover:bg-border-light'}
      value={id}
      onClick={() => onValueSelected(id)}
    >
      <div className="flex">
        <Checkbox
          className="mr-2"
          id={`selection-checkbox-${id}`}
          checked={isSelected}
          onChange={() => onValueSelected(id)}
        />
        {label}
      </div>
    </ActionList.Item>
  )
}

export const MultiSelectDropdown = ({
  values,
  selectedValues,
  setSelectedValues,
  formatSelectionCount = items => items?.length ? `${items.length} items` : 'Select an item',
  search = false,
  className = '',
  icon = undefined,
  isDisabled = false,
  hideCaret = false,
  hideBorder = false,
  dropdownLabel = '',
  bgColorOpen = 'border-dark',
  bgColorClosed = 'white',
  textColor = 'brand',
  iconFill = 'white',
  iconClass = '',
  iconSize = 32,
  buttonClass = '',
  popoverClass = '',
  direction = DROPDOWN_DIRECTIONS[0],
  useTailwindIconSizing = false,
  hideLabelInSmallViewports = true, // Added to preserve legacy logic
  buttonLabelSize = 'lg',
}) => {
  const { 
    getDetailsProps, 
    open, 
    // setOpen, 
  } = useDetails({ closeOnOutsideClick: true });

  const [listItems, setListItems] = useState([])

  useEffect(() => {
    setListItems(values)
  }, [values])

  const displayLabel = useMemo(() => `${dropdownLabel ? `${dropdownLabel} ` : ''}${formatSelectionCount(selectedValues)}`, [dropdownLabel, formatSelectionCount, selectedValues])
  const buttonProps = hideBorder ? {
    border: 0,
    padding: 3,
    bg: open ? getThemeColor(bgColorOpen) : getThemeColor(bgColorClosed),
    ':hover:not([disabled])': {
      bg: open ? getThemeColor(bgColorOpen) : getThemeColor(bgColorClosed),
    },
    ':active:not([disabled])': {
      bg: open ? getThemeColor(bgColorOpen) : getThemeColor(bgColorClosed),
    },
    boxShadow: 0,
  } : {};
  const iconFillColor = getThemeColor(iconFill) || iconFill;

  if (isDisabled) {
    return <Button variant={hideBorder ? 'invisible' : 'default'} disabled><span className={`text-${buttonLabelSize}`}>{displayLabel}</span></Button>
  }

  const handleSearchInput = (e) => {
    setListItems(values.filter(item => item.label?.toLowerCase()?.includes(e?.toLowerCase())))
  }
  
  const isValueSelected = (val) => {
    const found = selectedValues.find(({ id }) => id === val);
    return !!found;
  }
  
  const onSelectAll = () => setSelectedValues(values);
  
  const onDeselectAll = () => setSelectedValues([]);

  return (
    <Details
      className={
        classNames(
          'brand-select w-min appearance-none cursor-pointer',
          { 'bg-border-dark': open },
          { 'bg-white': !open },
          className
        )
      }
      {...getDetailsProps()}
    >
      <Button
        variant={hideBorder ? 'invisible' : 'default'}
        as="summary"
        sx={buttonProps}
      >
        <div className={classNames('flex justify-between items-center', buttonClass)}>
          {
            icon ? (
              <Icon className={iconClass} fill={iconFillColor} icon={icon} size={iconSize} useTailwindSizing={useTailwindIconSizing} />
            ) : null
          }
          {
            displayLabel?.length ? (
              <Label
                className={classNames(
                  { 'hidden lg:inline-block': hideLabelInSmallViewports },
                  { 'inline-block': !hideLabelInSmallViewports },
                )}
                size={buttonLabelSize}
                color={textColor}
              >
                {displayLabel}
              </Label>
            ) : null
          }
          {
            hideCaret ? null : (
              <div className="ml-2">
                {open ? <CaretUpIcon fill={textColor} /> : <CaretDownIcon fill={textColor} />}
              </div>
            )
          }
        </div>
      </Button>
      {open && <Popover
        style={{ zIndex: 50 }}
        className={classNames('text-brand', popoverClass)}
        sx={{ mt: 1 }}
        caret={direction}
        open={open}
      >
        <Popover.Content className={(listItems.length > 7) ? 'bg-white h-multi-select-dropdown' : 'bg-white'} style={{ left: '0px', overflow: 'scroll' }} sx={{ p: 1 }}>
          {
            search && <SearchInput
              className="w-full mt-1"
              onChange={e => handleSearchInput(e.target.value)}
              shadow={false}
              block
              hideBorder
            />
          }
          <ActionList.Divider key="search-divider" />
          <div className="mx-2 mt-2 flex justify-between">
            <LinkButton
              size="sm"
              color="link-blue"
              hoverColor="faded-brand"
              activeColor="highlighted"
              onClick={onSelectAll}
              showUnderlineOnHover
            >
              Select All
            </LinkButton>
            <LinkButton
              size="sm"
              color="link-red"
              hoverColor="faded-error"
              activeColor="error"
              onClick={onDeselectAll}
              showUnderlineOnHover
            >
              Deselect All
            </LinkButton>
          </div>
          <ActionList.Divider key="selection-control-divider" />
          {
            (listItems?.length) ? listItems.map(item => (
              item?.label !== ''
                ? <SelectionItem
                  key={item?.id}
                  id={item?.id}
                  label={item?.label}
                  render={item?.render}
                  isSelected={isValueSelected(item?.id)}
                  onValueSelected={() => {
                    if (!isFunction(setSelectedValues)) {
                      return;
                    }
                    if (!isValueSelected(item.id)) {
                      setSelectedValues([
                        ...selectedValues,
                        item,
                      ])
                    } else {
                      setSelectedValues(selectedValues.filter(({ id: valId }) => valId !== item.id))
                    }
                  }}
                />
                : <ActionList.Divider key={item?.id} />
            )) : null
          }
        </Popover.Content>
      </Popover>}
    </Details>
  )
}

export default MultiSelectDropdown;
